/**
 *
 * @module storage
 * @see http://developer.samsung.com/tv/develop/legacy-platform-library/ref00001/openCommonFile
 *
 */

/* global FileSystem */
/* global curWidget */

import Check from 'check-types';
import Util from './../../../util/entry';
import Logging from './../../../../logging/entry';
import CoreStorageProvider from './../../shared/coreStorageProvider';
import Exception from './../../../exception/entry';

const ProviderException = Exception.ProviderException;
const InvalidArgumentException = Exception.InvalidArgumentException;

const {
    assigned
} = Check.assert;

/**
 *
 * @class Storage
 * @extends {CoreStorageProvider}
 * @desc Samsung Orsay Storage provider implementation.
 * @note this will create an app directory locally on the device if it doesn't exist
 * @note curWidget should be available globally on Samsung Orsay
 *
 * @example
 * const storage = new Storage(logger);
 *
 */
export default class Storage extends CoreStorageProvider {

    /**
     *
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @throws {ProviderException}
     *
     */
    constructor(logger = Logging) {

        const errorMessage = {
            ctor: 'Storage(logger)',
            get fileSystem() {
                return `${this.ctor} expected FileSystem instance`;
            },
            get curWidget() {
                return `${this.ctor} expected curWidget instance`;
            }
        };

        const scope = Util.getScope();
        const fileSystemInstance = (function fileSystemInstance() {
            return Check.assigned(scope)
                && Check.assigned(scope.FileSystem)
                && Check.function(new scope.FileSystem().openCommonFile);
        }());

        const curWidgetInstance = (function curWidgetInstance() {
            return Check.assigned(scope)
                && Check.assigned(scope.curWidget)
                && Check.assigned(scope.curWidget.id);
        }());

        Check.assert(
            fileSystemInstance,
            errorMessage.fileSystem,
            ProviderException
        );

        Check.assert(
            curWidgetInstance,
            errorMessage.curWidget,
            ProviderException
        );

        const fileSystem = new scope.FileSystem();
        const widgetId = scope.curWidget.id;

        /**
         *
         * @note creates app directory if it doesn't exist
         *
         */
        if (!fileSystem.isValidCommonPath(widgetId)) {
            fileSystem.createCommonDir(widgetId);
        }

        super(logger);

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.fileSystem = fileSystem;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.fileName = 'bam-sdk.db';

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.samsungFileName = `${widgetId}/${this.fileName}`;

        const fileStorage = this.samsungLocalStore();
        const hasFileStorage = Check.assigned(fileStorage);
        const storeMessage = hasFileStorage ? 'Reading existing file storage.' : 'Creating new file storage';

        /**
         *
         * @access private
         * @type {Object}
         * @desc storage reference
         *
         */
        this.store = fileStorage || {};

        this.logger.log('Storage', 'Created Samsung Orsay Provider.');
        this.logger.info('Storage', storeMessage);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @param {String} signature - the current active method signature
     * @param {String} key - Storage key reference
     * @param {*} value - value to store with key
     * @returns {Promise<*>}
     *
     */
    op(signature, key, value) {

        const errorMessage = {
            get operationError() {
                return `${signature} cannot complete successfully`;
            },
            get keyError() {
                return `${signature} key should be a String with length`;
            },
            get keyMissingWarning() {
                return `${signature} reference cache key "${key}" does not exist`;
            },
            get valueParseError() {
                return `${signature} unable to parse value`;
            },
            get storeError() {
                return `${signature} missing expected store reference`;
            }
        };

        const operation = signature.slice(signature.indexOf('.') + 1, signature.indexOf('('));
        const isClear = Check.equal(operation, 'clear');
        const isRemove = Check.equal(operation, 'remove');

        const store = this.store;

        assigned(store, errorMessage.storeError, ProviderException);

        return new Promise((resolve, reject) => {

            const { logger, storedKeys } = this;

            /**
             *
             * @desc verify key validity for all operations except clear
             *
             */
            if (Check.equal(isClear, false) && (Check.not.string(key) || Check.emptyString(key))) {
                return reject(new InvalidArgumentException(errorMessage.keyError));
            }

            /**
             *
             * @desc process given operation
             * @note all items set in Storage are expected to be stringified values
             * @note JSON operations are processed in a try...catch
             * @note if a given key is not found, fail silently and display warning
             *
             */
            try {

                if (isClear) {

                    // remove all known keys
                    storedKeys.map((keyInStorage) => {
                        return delete store[keyInStorage];
                    });

                    this.saveSamsungLocalStorage();

                    return resolve();

                }

                if (isRemove) {

                    // remove item and key from reference
                    storedKeys.splice(storedKeys.indexOf(key), 1);

                    delete store[key];
                    this.saveSamsungLocalStorage();

                    return resolve();

                } else if (Check.not.undefined(value)) {

                    // set item if valid value provided
                    // stringify before updating stored keys reference for accuracy
                    const val = JSON.stringify(value);

                    storedKeys.push(key);

                    store[key] = val;
                    this.saveSamsungLocalStorage();

                    return resolve();

                } else {

                    // if valid value not provided, attempt to get cached item
                    try {

                        const item = store[key];

                        if (Check.not.assigned(item)) {
                            logger.warn('Storage', errorMessage.keyMissingWarning);
                            return resolve();
                        }

                        return resolve(JSON.parse(item));

                    } catch (error) {

                        return reject(new InvalidArgumentException(`${errorMessage.valueParseError} - ${error}`));

                    }

                }

            } catch (error) {

                return reject(new ProviderException(`${errorMessage.operationError} - ${error}`));

            }

        });

    }

    /**
     *
     * @access private
     * @desc write to Samsung filesystem
     *
     */
    saveSamsungLocalStorage() {

        const fileObj = this.fileSystem.openCommonFile(this.samsungFileName, 'w');

        fileObj.writeAll(JSON.stringify(this.store));

        this.fileSystem.closeCommonFile(fileObj);

    }

    /**
     *
     * @access private
     * @desc Reads from the local Samsung file system in order to fetch stored data.
     * If no file reference is found, returns null to signify the need for fallback logic.
     * @returns {Object|null}
     *
     */
    samsungLocalStore() {

        try {

            // r+ : Open a file for update (both reading and writing). The file must exist.
            const fileObj = this.fileSystem.openCommonFile(this.samsungFileName, 'r+');

            if (Check.assigned(fileObj)) {

                this.logger.log('Storage', 'Common file found, fetching cached data.');

                const dataStr = fileObj.readAll();

                this.fileSystem.closeCommonFile(fileObj);

                return JSON.parse(dataStr);

            }

            return null;

        } catch (error) {

            this.logger.error('Storage', `Failed to read file ${error}`);

            return null;

        }

    }

}



// WEBPACK FOOTER //
// ./src/services/platforms/samsung/orsay/storage.js